import { Patient } from '../types';
import { Database, dbToPatient, patientToDb } from './supabaseClient';
import { fetchAll, fetchById, createRecord, updateRecord, deleteRecord, getCurrentUser } from './baseService';
import { supabase, isSupabaseAvailable } from './supabaseClient';

// ============================================
// PATIENT SERVICE
// ============================================

type PatientRow = Database['public']['Tables']['patients']['Row'];

/**
 * Get all patients for the current user (non-archived by default)
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getPatients = async (includeArchived: boolean = false): Promise<Patient[]> => {
  const filters = includeArchived ? [] : [{ column: 'archived', value: false }];

  return fetchAll<PatientRow, Patient>(
    'patients',
    dbToPatient,
    {
      orderBy: 'name',
      ascending: true,
      filters
    }
  );
};

/**
 * Get a single patient by ID
 */
export const getPatientById = async (id: string): Promise<Patient | null> => {
  return fetchById<PatientRow, Patient>('patients', id, dbToPatient);
};

/**
 * Create a new patient
 * SECURITY: Explicitly sets user_id from authenticated user
 */
export const createPatient = async (
  patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Patient | null> => {
  return createRecord<PatientRow, Patient, Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>>(
    'patients',
    patientData,
    patientToDb,
    dbToPatient
  );
};

/**
 * Update an existing patient
 */
export const updatePatient = async (
  id: string,
  updates: Partial<Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<Patient | null> => {
  const dbUpdates: Record<string, unknown> = {};

  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.dateOfBirth !== undefined) dbUpdates.date_of_birth = updates.dateOfBirth || null;
  if (updates.gender !== undefined) dbUpdates.gender = updates.gender || null;
  if (updates.email !== undefined) dbUpdates.email = updates.email || null;
  if (updates.phone !== undefined) dbUpdates.phone = updates.phone || null;
  if (updates.medicalRecordNumber !== undefined) dbUpdates.medical_record_number = updates.medicalRecordNumber || null;
  if (updates.notes !== undefined) dbUpdates.notes = updates.notes || null;
  if (updates.archived !== undefined) dbUpdates.archived = updates.archived;
  // Demographic
  if (updates.educationLevel !== undefined) dbUpdates.education_level = updates.educationLevel || null;
  if (updates.occupation !== undefined) dbUpdates.occupation = updates.occupation || null;
  if (updates.maritalStatus !== undefined) dbUpdates.marital_status = updates.maritalStatus || null;
  if (updates.socioeconomicContext !== undefined) dbUpdates.socioeconomic_context = updates.socioeconomicContext || null;
  if (updates.livingArrangement !== undefined) dbUpdates.living_arrangement = updates.livingArrangement || null;
  // Psychiatric History
  if (updates.previousDiagnoses !== undefined) dbUpdates.previous_diagnoses = updates.previousDiagnoses || null;
  if (updates.previousTreatments !== undefined) dbUpdates.previous_treatments = updates.previousTreatments || null;
  if (updates.psychiatricHospitalizations !== undefined) dbUpdates.psychiatric_hospitalizations = updates.psychiatricHospitalizations || null;
  if (updates.suicideAttempts !== undefined) dbUpdates.suicide_attempts = updates.suicideAttempts || null;
  // Medical History
  if (updates.chronicConditions !== undefined) dbUpdates.chronic_conditions = updates.chronicConditions || null;
  if (updates.currentMedications !== undefined) dbUpdates.current_medications = updates.currentMedications || null;
  if (updates.headTraumaHistory !== undefined) dbUpdates.head_trauma_history = updates.headTraumaHistory || null;
  if (updates.substanceUse !== undefined) dbUpdates.substance_use = updates.substanceUse || null;
  // Family History
  if (updates.familyMentalHealthHistory !== undefined) dbUpdates.family_mental_health_history = updates.familyMentalHealthHistory || null;
  if (updates.familySuicideHistory !== undefined) dbUpdates.family_suicide_history = updates.familySuicideHistory || null;
  // Current Consultation
  if (updates.consultationReason !== undefined) dbUpdates.consultation_reason = updates.consultationReason || null;
  if (updates.symptomDuration !== undefined) dbUpdates.symptom_duration = updates.symptomDuration || null;
  if (updates.recentStressors !== undefined) dbUpdates.recent_stressors = updates.recentStressors || null;
  if (updates.functionalImpact !== undefined) dbUpdates.functional_impact = updates.functionalImpact || null;
  // Previous Evaluations
  if (updates.previousTestResults !== undefined) dbUpdates.previous_test_results = updates.previousTestResults || null;
  if (updates.previousReports !== undefined) dbUpdates.previous_reports = updates.previousReports || null;
  if (updates.formalDiagnoses !== undefined) dbUpdates.formal_diagnoses = updates.formalDiagnoses || null;
  // Clinical Observations
  if (updates.collaborationLevel !== undefined) dbUpdates.collaboration_level = updates.collaborationLevel || null;
  if (updates.fatigueObservations !== undefined) dbUpdates.fatigue_observations = updates.fatigueObservations || null;
  if (updates.spontaneousComments !== undefined) dbUpdates.spontaneous_comments = updates.spontaneousComments || null;
  if (updates.observableBehavior !== undefined) dbUpdates.observable_behavior = updates.observableBehavior || null;

  return updateRecord<PatientRow, Patient>('patients', id, dbUpdates, dbToPatient);
};

/**
 * Archive a patient (soft delete)
 */
export const archivePatient = async (id: string): Promise<boolean> => {
  const result = await updatePatient(id, { archived: true });
  return result !== null;
};

/**
 * Unarchive a patient
 */
export const unarchivePatient = async (id: string): Promise<boolean> => {
  const result = await updatePatient(id, { archived: false });
  return result !== null;
};

/**
 * Delete a patient permanently (also deletes all associated test results via CASCADE)
 */
export const deletePatient = async (id: string): Promise<boolean> => {
  return deleteRecord('patients', id);
};

/**
 * Search patients by name or medical record number (current user's patients only)
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const searchPatients = async (query: string): Promise<Patient[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('No authenticated user, cannot search patients');
      return [];
    }

    const { data, error } = await supabase!
      .from('patients')
      .select('*')
      .eq('user_id', user.id)
      .or(`name.ilike.%${query}%,medical_record_number.ilike.%${query}%`)
      .eq('archived', false)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error searching patients:', error);
      return [];
    }

    return (data || []).map(dbToPatient);
  } catch (err) {
    console.error('Failed to search patients:', err);
    return [];
  }
};
